import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Register = () => {
  const [formData, setFormData] = useState({
    business_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    website: '',
    reason: ''
  });
  const [document, setDocument] = useState(null);
  const [profilePicture, setProfilePicture] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { user, register } = useAuth();
  const navigate = useNavigate();

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    if (e.target.name === 'document') {
      setDocument(e.target.files[0]);
    } else if (e.target.name === 'profile_picture') {
      setProfilePicture(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (!profilePicture) {
      setError('Profile picture is required');
      setLoading(false);
      return;
    }

    const submitData = new FormData();
    Object.keys(formData).forEach(key => {
      if (key !== 'confirmPassword') {
        submitData.append(key, formData[key]);
      }
    });

    if (document) {
      submitData.append('document', document);
    }

    if (profilePicture) {
      submitData.append('profile_picture', profilePicture);
    }

    const result = await register(submitData);
    
    if (result.success) {
      // Redirect to login page after successful registration
      navigate('/login', { 
        state: { 
          message: result.message 
        }
      });
    } else {
      setError(result.error);
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img src="/favicon.ico" alt="Skilltell" className="h-16 w-16" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-primary-600">
          Join Skilltell
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Register your business for advertising
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-skilltell-lg rounded-xl sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="text-sm text-red-600">{error}</div>
              </div>
            )}

            <div>
              <label htmlFor="business_name" className="block text-sm font-medium text-gray-700">
                Business Name *
              </label>
              <input
                id="business_name"
                name="business_name"
                type="text"
                required
                className="mt-1 input-field"
                value={formData.business_name}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="profile_picture" className="block text-sm font-medium text-gray-700">
                Profile Picture *
              </label>
              <input
                id="profile_picture"
                name="profile_picture"
                type="file"
                accept=".jpg,.jpeg,.png"
                required
                className="mt-1 input-field"
                onChange={handleFileChange}
              />
              {profilePicture && (
                <p className="mt-2 text-sm text-green-600">
                  Selected: {profilePicture.name} ({(profilePicture.size / (1024 * 1024)).toFixed(2)} MB)
                </p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Upload your business logo or profile picture (JPG, PNG - Max 10MB)
              </p>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address *
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="mt-1 input-field"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                Phone Number *
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                required
                className="mt-1 input-field"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="website" className="block text-sm font-medium text-gray-700">
                Website
              </label>
              <input
                id="website"
                name="website"
                type="url"
                className="mt-1 input-field"
                value={formData.website}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password *
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="mt-1 input-field"
                value={formData.password}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password *
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                className="mt-1 input-field"
                value={formData.confirmPassword}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="reason" className="block text-sm font-medium text-gray-700">
                Reason for Advertising
              </label>
              <textarea
                id="reason"
                name="reason"
                rows="3"
                className="mt-1 input-field"
                value={formData.reason}
                onChange={handleChange}
                placeholder="Tell us about your advertising goals..."
              />
            </div>

            <div>
              <label htmlFor="document" className="block text-sm font-medium text-gray-700">
                Business Document (Optional)
              </label>
              <input
                id="document"
                name="document"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                className="mt-1 input-field"
                onChange={handleFileChange}
              />
              <p className="mt-1 text-sm text-gray-500">
                Upload business license, tax ID, or other verification document (PDF, JPG, PNG)
              </p>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="spinner mr-2"></div>
                    Creating account...
                  </div>
                ) : (
                  'Create Account'
                )}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Already have an account?</span>
              </div>
            </div>

            <div className="mt-6">
              <Link
                to="/login"
                className="w-full btn-outline text-center block"
              >
                Sign in instead
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;